import callsTextingService from '@/modules/callsTexting/services/callsTextingService';
import { i18n } from '@/plugins/i18n';
import { DEFAULT_COUNTRY_CODE, PROMOTIONAL_VERIFICATION_STATUS } from '@/modules/texting/constants/textingConstants';
import { isUSTollFreeNumber } from '@/utils/phoneNumberUtils';

const helpers = {
  getInstalledPMCApp: (rootGetters) => {
    const pmcAppName = 'pickmycall';
    const pmcAppInstalled = rootGetters['AppsStore/appByCodeName'](pmcAppName)?.status === 'installed';
    if (pmcAppInstalled) return pmcAppName;

    const pmcFreeAppName = 'pickmycallfree';
    const pmcFreeAppInstalled = rootGetters['AppsStore/appByCodeName'](pmcFreeAppName)?.status === 'installed';
    if (pmcFreeAppInstalled) return pmcFreeAppName;
    return 'NULL';
  },
  getDefaultSettings: (businessName) => ({
    voice_scripts: {
      missed_call_message: {
        enabled: false,
        message: i18n.t('voice_texting_settings.call_forwarding.missed_calls.default_message', { businessName }),
      },
      voicemail_message: {
        enabled: true,
        message: i18n.t('voice_texting_settings.call_forwarding.missed_calls.voicemail_default_message', { businessName }),
      },
      greeting_message: {
        enabled: false,
        message: i18n.t('voice_texting_settings.call_forwarding.greeting_message.default_message', { businessName }),
      },
      recording_call_message: {
        enabled: false,
        disable_consent: true,
        message: i18n.t('voice_texting_settings.call_forwarding.recording_message.message_option1_no_consent'),
      },
      not_available_message: {
        message: i18n.t('voice_texting_settings.call_forwarding.not_available_message.default_message', { businessName }),
      },
    },
  }),
};

const state = {
  callsSettings: null,
  quotaBalance: null,
  quotaCredit: null,
  activeDedicatedNumber: null,
  showBusinessInfoError: false,
  availablePhoneNumbers: [],
  totalAvailablePhoneNumbers: null,
  availableTollFreeNumbers: [],
  totalAvailableTollFreeNumbers: null,
  businessPhoneNumbers: [],
};

const getters = {
  callsSettings: (state) => state.callsSettings,
  quotaCredit: (state) => state.quotaCredit,
  quotaBalance: (state) => state.quotaBalance,
  activeDedicatedNumber: (state) => state.activeDedicatedNumber,
  showBusinessInfoError: (state) => state.showBusinessInfoError,
  businessPhoneNumbers: (state) => state.businessPhoneNumbers,
  businessPhoneNumbersByFeature: (state) => (feature) => state.businessPhoneNumbers?.filter((number) => number.features.includes(feature)),
  existingTollFreeNumber: (state) => state.businessPhoneNumbers?.find((number) => isUSTollFreeNumber(number?.phone_number)) || null,
  availablePhoneNumbers: (state) => state.availablePhoneNumbers,
  totalAvailablePhoneNumbers: (state) => state.totalAvailablePhoneNumbers,
  availableTollFreeNumbers: (state) => state.availableTollFreeNumbers,
  totalAvailableTollFreeNumbers: (state) => state.totalAvailableTollFreeNumbers,
  defaultSettings: () => (businessName) => helpers.getDefaultSettings(businessName),
  // SMS Campaigns related getters
  promotionalVerificationStatus: (state) => state.activeDedicatedNumber?.promotional_verification_status || null,
  campaignSms: (state) => state.activeDedicatedNumber?.verification_request_data?.campaign_sms || false,
  campaignExample: (state) => state.activeDedicatedNumber?.verification_request_data?.campaign_example || '',
  existingVerificationData: (state) => state.activeDedicatedNumber?.verification_request_data || null,
  isPendingPromotionalVerification: (state) => state.activeDedicatedNumber?.promotional_verification_status === PROMOTIONAL_VERIFICATION_STATUS.PENDING,
};

const mutations = {
  setShowBusinessInfoError(state, show) {
    state.showBusinessInfoError = show;
  },
  setCallsSettings(state, settings) {
    state.callsSettings = settings;
  },
  setCallQuota(state, { credit, balance }) {
    state.quotaCredit = credit;
    state.quotaBalance = balance;
  },
  setActiveDedicatedNumber(state, activeDedicatedNumber) {
    state.activeDedicatedNumber = activeDedicatedNumber;
  },
  setAvailablePhoneNumbers(state, availablePhoneNumbers) {
    state.availablePhoneNumbers = availablePhoneNumbers;
  },
  setTotalAvailablePhoneNumbers(state, total) {
    state.totalAvailablePhoneNumbers = total;
  },
  setBusinessPhoneNumbers(state, businessPhoneNumbers) {
    state.businessPhoneNumbers = businessPhoneNumbers;
  },
  setAvailableTollFreeNumbers(state, numbers) {
    state.availableTollFreeNumbers = numbers;
  },
  setTotalAvailableTollFreeNumbers(state, total) {
    state.totalAvailableTollFreeNumbers = total;
  },
};

const actions = {
  setShowBusinessInfoError({ commit }, show) {
    commit('setShowBusinessInfoError', show);
  },
  async updateCallsSettings({ commit, state }, updatedSettings) {
    const result = await callsTextingService.updateCallsSettings(state.callsSettings.uid, updatedSettings);
    commit('setCallsSettings', result.data);
  },
  async fetchCallsSettings({ commit }, { dedicatedNumber, feature } = {}) {
    const result = await callsTextingService.getCallsSettings(dedicatedNumber, feature);
    if (result.success) {
      commit('setCallsSettings', result.data.voice_call_settings[0]);
    }
  },
  async fetchCallsSettingsForVoiceApp({ dispatch }, { dedicatedNumber } = {}) {
    return dispatch('fetchCallsSettings', { dedicatedNumber, feature: 'VOICE' });
  },
  async fetchCallsSettingsForCallsAndTexting({ dispatch }, { dedicatedNumber } = {}) {
    return dispatch('fetchCallsSettings', { dedicatedNumber });
  },
  async getRemainingQuota({ commit }) {
    const result = await callsTextingService.getRemainingQuota();
    if (result.success && result.data.voice_call_quotas[0]) {
      commit('setCallQuota', result.data.voice_call_quotas[0]);
    }
  },
  async fetchActiveDedicatedNumber({ commit }, params) {
    try {
      const activeDedicatedNumber = await callsTextingService.getBusinessActiveDedicatedNumber(params);
      commit('setActiveDedicatedNumber', activeDedicatedNumber);
      return activeDedicatedNumber;
    } catch (err) {
      return err;
    }
  },
  async setTextingStatus({ commit, state }, status) {
    const { activeDedicatedNumber } = state;
    if (!activeDedicatedNumber?.uid) {
      throw new Error('No active dedicated number found');
    }
    // Convert status string to API format (ACTIVE/SMS_INACTIVE)
    const apiStatus = status === 'active' ? 'ACTIVE' : 'SMS_INACTIVE';
    // Include phone_number and features as the API might require them
    await callsTextingService.updateBusinessPhoneNumber(activeDedicatedNumber.uid, {
      status: apiStatus,
      phone_number: activeDedicatedNumber.phone_number,
      features: activeDedicatedNumber.features || ['VOICE', 'SMS'],
    });
    commit('setActiveDedicatedNumber', { ...activeDedicatedNumber, status: apiStatus });
  },
  async claimNumber({ commit }, { voiceTextAppInstalled }) {
    try {
      const activeDedicatedNumber = await callsTextingService.claimNumber({ voiceTextAppInstalled });
      commit('setActiveDedicatedNumber', activeDedicatedNumber);
      return activeDedicatedNumber;
    } catch (err) {
      return err;
    }
  },
  async fetchAvailablePhoneNumbers({ commit }, { areaCode, countryCode, page, perPage, features }) {
    try {
      const availablePhoneNumbersObj = await callsTextingService.getAvailablePhoneNumbers(
        { areaCode, countryCode, page, perPage, features },
      );
      commit('setAvailablePhoneNumbers', availablePhoneNumbersObj.available_phone_numbers.map((number) => ({ ...number, area_code: areaCode })));
      commit('setTotalAvailablePhoneNumbers', availablePhoneNumbersObj.total);
      return availablePhoneNumbersObj;
    } catch (err) {
      return err;
    }
  },
  async createBusinessPhoneNumber({ commit, state }, { phoneNumber, countryCode, features }) {
    const result = await callsTextingService.createBusinessPhoneNumber({ phoneNumber, countryCode, features });
    commit('setBusinessPhoneNumbers', [...state.businessPhoneNumbers, result.data.data]);
  },
  async fetchBusinessPhoneNumbers({ commit }) {
    try {
      const result = await callsTextingService.getBusinessPhoneNumbers();
      commit('setBusinessPhoneNumbers', result.business_phone_numbers);
      return result.business_phone_numbers;
    } catch (err) {
      return err;
    }
  },
  clearAvailablePhoneNumbers({ commit }) {
    commit('setAvailablePhoneNumbers', []);
    commit('setTotalAvailablePhoneNumbers', null);
    commit('setAvailableTollFreeNumbers', []);
    commit('setTotalAvailableTollFreeNumbers', null);
  },

  async getAvailableVoiceSmsNumbers(context, countryCode = DEFAULT_COUNTRY_CODE) {
    return callsTextingService.getAvailableVoiceSmsNumbers(countryCode);
  },

  async fetchAvailableTollFreeNumbers(
    { commit },
    { countryCode = DEFAULT_COUNTRY_CODE, page = 1, perPage, features = ['VOICE'] } = {},
  ) {
    try {
      const response = await callsTextingService.getAvailablePhoneNumbers({
        countryCode,
        page,
        perPage,
        features,
      });
      commit('setAvailableTollFreeNumbers', response?.available_phone_numbers || []);
      commit('setTotalAvailableTollFreeNumbers', response?.total ?? 0);
      return response;
    } catch (err) {
      commit('setAvailableTollFreeNumbers', []);
      commit('setTotalAvailableTollFreeNumbers', 0);
      return err;
    }
  },

  async claimPhoneNumberWithVerification({ commit }, {
    enableTexting = false,
    verificationRequestData = null,
    countryCode = DEFAULT_COUNTRY_CODE,
    phoneNumber = null, // Optional: pre-selected phone number
  }) {
    let numberToClaim = phoneNumber;

    if (!numberToClaim) {
      const availableNumbers = await callsTextingService.getAvailableVoiceSmsNumbers(countryCode);

      if (!availableNumbers || availableNumbers.length === 0) {
        throw new Error('No available phone numbers');
      }

      numberToClaim = availableNumbers[0].phone_number;
    }

    // Claim the number with appropriate settings
    const claimedNumber = await callsTextingService.claimPhoneNumberWithVerification({
      phoneNumber: numberToClaim,
      countryCode,
      enableTexting,
      verificationRequestData,
    });

    // Update store with the claimed number
    commit('setActiveDedicatedNumber', claimedNumber);

    return claimedNumber;
  },

  async updateExistingNumberWithTexting({ commit, state }, { verificationRequestData, enableTexting }) {
    const { activeDedicatedNumber } = state;
    if (!activeDedicatedNumber?.uid) {
      throw new Error('No active dedicated number to update');
    }

    const updateData = {
      status: enableTexting ? 'ACTIVE' : 'SMS_INACTIVE',
      phone_number: activeDedicatedNumber.phone_number,
      features: activeDedicatedNumber.features || ['VOICE', 'SMS'],
    };

    if (enableTexting && verificationRequestData) {
      updateData.verification_request_data = verificationRequestData;
    }

    const updatedNumber = await callsTextingService.updateBusinessPhoneNumber(
      activeDedicatedNumber.uid,
      updateData,
    );

    commit('setActiveDedicatedNumber', {
      ...activeDedicatedNumber,
      ...updatedNumber,
      status: updateData.status,
    });

    return updatedNumber;
  },

  async updateCampaignSmsSettings({ commit, state }, { campaignSms, campaignExample }) {
    const { activeDedicatedNumber } = state;
    if (!activeDedicatedNumber?.uid) {
      throw new Error('No active dedicated number to update');
    }

    // Merge with existing verification data to avoid API errors
    const existingVerificationData = activeDedicatedNumber.verification_request_data || {};
    const updatedData = {
      verification_request_data: {
        ...existingVerificationData,
        campaign_sms: campaignSms,
        campaign_example: campaignExample,
      },
    };

    const updatedNumber = await callsTextingService.updateBusinessPhoneNumber(
      activeDedicatedNumber.uid,
      updatedData,
    );

    // Update store with the updated number
    commit('setActiveDedicatedNumber', {
      ...activeDedicatedNumber,
      ...updatedNumber,
      verification_request_data: updatedData.verification_request_data,
      promotional_verification_status: updatedNumber?.promotional_verification_status || activeDedicatedNumber.promotional_verification_status,
    });

    return updatedNumber;
  },
};
export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
